(function () {
  const ENDPOINT_BASE = 'https://a.klaviyo.com/client';
  const REVISION = '2025-01-15';
  const VARIANT_ID_PREFIX = '$shopify:::$default:::';

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const setMessage = (messageEl, text, isError) => {
    if (!messageEl) return;
    messageEl.textContent = text || '';
    messageEl.hidden = !text;
    messageEl.classList.toggle('message--error', Boolean(isError));
  };

  const subscribeBackInStock = async ({ companyId, variantId, email, genericError }) => {
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `${VARIANT_ID_PREFIX}${variantId}`,
            },
          },
        },
      },
    };

    const response = await fetch(
      `${ENDPOINT_BASE}/back-in-stock-subscriptions?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: REVISION,
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) return;

    let errorMessage = genericError;
    try {
      const errorPayload = await response.json();
      if (errorPayload && errorPayload.message) {
        errorMessage = errorPayload.message;
      }
    } catch (_error) {
      // Keep fallback message.
    }

    throw new Error(errorMessage);
  };

  const subscribeMarketing = async ({ companyId, listId, email }) => {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    await fetch(`${ENDPOINT_BASE}/subscriptions?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: REVISION,
      },
      body: JSON.stringify(payload),
    });
  };

  const controllers = new Map();

  const initForm = (formEl) => {
    const containerEl = formEl.closest('[data-back-in-stock-container]');
    if (!containerEl || !containerEl.id) return;

    const emailEl = formEl.querySelector('[data-back-in-stock-email]');
    const marketingEl = formEl.querySelector('[data-back-in-stock-marketing]');
    const submitEl = formEl.querySelector('[data-back-in-stock-submit]');
    const messageEl = formEl.querySelector('[data-back-in-stock-message]');
    const panelEl = formEl.querySelector('[data-back-in-stock-panel]');
    const toggleButton = document.querySelector(
      `[data-back-in-stock-toggle-target="${containerEl.id}"]`
    );

    const labels = {
      invalidEmail: formEl.dataset.invalidEmailLabel || 'Please enter a valid email.',
      missingConfig:
        formEl.dataset.missingConfigLabel || 'Back in stock is unavailable right now.',
      genericError: formEl.dataset.genericErrorLabel || 'Something went wrong.',
      success:
        formEl.dataset.successLabel ||
        'Thank you for registering. We will notify you when this size becomes available.',
    };

    const state = {
      isSubmitting: false,
      companyId: formEl.dataset.companyId || '',
      listId: formEl.dataset.listId || '',
      currentVariantId: formEl.dataset.currentVariantId || '',
      currentVariantAvailable: containerEl.dataset.currentVariantAvailable === 'true',
      hasOpenedOnce: false,
      isPanelOpen: false,
    };

    const updateVisibility = () => {
      const isOutOfStock = !state.currentVariantAvailable;
      if (panelEl) panelEl.hidden = !isOutOfStock || !state.isPanelOpen;
    };

    const openPanel = () => {
      state.hasOpenedOnce = true;
      state.isPanelOpen = true;
      updateVisibility();
      if (emailEl) emailEl.focus();
    };

    controllers.set(containerEl.id, {
      state,
      formEl,
      containerEl,
      openPanel,
      updateVisibility,
    });

    updateVisibility();

    formEl.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (state.isSubmitting) return;

      const email = emailEl ? emailEl.value.trim() : '';
      const notifyOther = Boolean(marketingEl && marketingEl.checked);

      setMessage(messageEl, '', false);

      if (!isValidEmail(email)) {
        setMessage(messageEl, labels.invalidEmail, true);
        return;
      }

      if (!state.companyId || !state.currentVariantId) {
        setMessage(messageEl, labels.missingConfig, true);
        return;
      }

      state.isSubmitting = true;
      if (submitEl) {
        submitEl.disabled = true;
        submitEl.setAttribute('aria-busy', 'true');
      }

      try {
        await subscribeBackInStock({
          companyId: state.companyId,
          variantId: state.currentVariantId,
          email,
          genericError: labels.genericError,
        });

        if (notifyOther && state.listId) {
          await subscribeMarketing({
            companyId: state.companyId,
            listId: state.listId,
            email,
          });
        }

        if (emailEl) emailEl.value = '';
        if (marketingEl) marketingEl.checked = false;
        setMessage(messageEl, labels.success, false);
      } catch (error) {
        const message = error && error.message ? error.message : labels.genericError;
        setMessage(messageEl, message, true);
      } finally {
        state.isSubmitting = false;
        if (submitEl) {
          submitEl.disabled = false;
          submitEl.setAttribute('aria-busy', 'false');
        }
      }
    });
  };

  const initToggleButtons = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-back-in-stock-toggle-target]');
      if (!button) return;

      const targetId = button.getAttribute('data-back-in-stock-toggle-target');
      if (!targetId) return;

      const controller = controllers.get(targetId);
      if (!controller) return;
      controller.openPanel();
      controller.containerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const initVariantChangeListener = () => {
    document.addEventListener('cascade:product:variantchange', (event) => {
      const variant = event.detail && event.detail.variant ? event.detail.variant : null;
      if (!variant || !variant.id) return;

      controllers.forEach((controller) => {
        controller.state.currentVariantId = String(variant.id);
        controller.formEl.dataset.currentVariantId = String(variant.id);
        controller.state.currentVariantAvailable = Boolean(variant.available);

        // Match legacy: once opened, keep panel open for out-of-stock variants.
        if (!controller.state.currentVariantAvailable && controller.state.hasOpenedOnce) {
          controller.state.isPanelOpen = true;
        }
        if (controller.state.currentVariantAvailable) {
          controller.state.isPanelOpen = false;
        }

        controller.updateVisibility();
      });
    });
  };

  const init = () => {
    document.querySelectorAll('[data-back-in-stock-form]').forEach(initForm);
    initToggleButtons();
    initVariantChangeListener();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
